import React, { useEffect, useState } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import AccountsPackItemTable from '../../../components/tables/AccountsPackItemTable';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import { useAccountsPackItemApi } from '../../../hooks/useAccountsPackItemApi';
import { useFileApi } from '../../../hooks/useFileApi';
import AddNewButton from '../../../components/common/AddButton';
import { confirmDelete, showAlert } from '../../../utils/sweetAlertUtils';
import { AccountsPackItemDTO } from '../../../services/apiClient';
import { useNavigate } from 'react-router-dom';
import Drawer from '../../../components/common/Drawer';
import { Download } from 'lucide-react';

const AccountsPackItemList: React.FC = () => {
  const { search, remove, extract, bulkDownload, isLoading, error } = useAccountsPackItemApi();
  const { getFileUrl } = useFileApi();
  const [items, setItems] = useState<AccountsPackItemDTO[]>([]);
  const [thumbnailUrls, setThumbnailUrls] = useState<Record<string, string>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalRecords, setTotalRecords] = useState(0);
  
  // Bulk download state
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkDownloadDrawerOpen, setBulkDownloadDrawerOpen] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [formErrors, setFormErrors] = useState<{ email?: string; name?: string }>({});
  
  // Search filters - default to current year and month
  const currentDate = new Date();
  const [emailSubject, setEmailSubject] = useState('');
  const [emailFrom, setEmailFrom] = useState('');
  const [periodYear, setPeriodYear] = useState(currentDate.getFullYear().toString());
  const [periodMonth, setPeriodMonth] = useState((currentDate.getMonth() + 1).toString());

  const navigate = useNavigate();
  
  // Generate years: current year ± 5 years
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 11 }, (_, i) => {
    const year = currentYear - 5 + i;
    return { value: year.toString(), label: year.toString() };
  });

  // Month options with friendly names
  const monthOptions = [
    { value: '1', label: 'January' },
    { value: '2', label: 'February' },
    { value: '3', label: 'March' },
    { value: '4', label: 'April' },
    { value: '5', label: 'May' },
    { value: '6', label: 'June' },
    { value: '7', label: 'July' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
  ];
  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Accounts Pack Items', href: '#' },
  ];

  const fetchThumbnails = async (items: AccountsPackItemDTO[]) => {
    const urls: Record<string, string> = {};
    
    for (const item of items) {
      if (item.fileRecordId) {
        try {
          const response = await getFileUrl(item.fileRecordId);
          if (response?.data?.url) {
            urls[item.id || ''] = response.data.url;
          }
        } catch (error) {
          console.error(`Error fetching thumbnail for ${item.fileRecordId}:`, error);
        }
      }
    }
    
    setThumbnailUrls(urls);
  };

  const fetchItems = async (page: number, size: number, filters: any) => {
    try {
      const response = await search({
        filters,
        page,
        pageSize: size,
        sortBy: 'CreatedUtc',
        sortDescending: true,
      });
      const results = response?.data?.results || [];
      setItems(results);
      setTotalRecords(response?.data?.totalCount || 0);
      
      // Fetch thumbnails for the items
      await fetchThumbnails(results);
    } catch (error) {
      console.error('Error fetching Accounts Pack Items:', error);
    }
  };

  useEffect(() => {
    const filters: any = {};
    if (periodYear) filters.periodYear = parseInt(periodYear);
    if (periodMonth) filters.periodMonth = parseInt(periodMonth);
    fetchItems(currentPage, pageSize, filters);
  }, [currentPage, pageSize, periodYear, periodMonth]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    const filters: any = {};
    if (emailSubject) filters.emailSubject = emailSubject;
    if (emailFrom) filters.emailFrom = emailFrom;
    if (periodYear) filters.periodYear = parseInt(periodYear);
    if (periodMonth) filters.periodMonth = parseInt(periodMonth);
    fetchItems(1, pageSize, filters);
  };

  const handleClearSearch = () => {
    setEmailSubject('');
    setEmailFrom('');
    setPeriodYear('');
    setPeriodMonth('');
    fetchItems(1, pageSize, {});
  };

  const handleDelete = async (dto: AccountsPackItemDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      await remove(dto.id || '');
      await fetchItems(currentPage, pageSize, {});
    } catch (error) {
      console.error('Error deleting AccountsPackItem:', error);
    }
  };

  const validateBulkDownloadForm = (): boolean => {
    const errors: { email?: string; name?: string } = {};
    
    if (!userEmail.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userEmail)) {
      errors.email = 'Invalid email format';
    }
    
    if (!userName.trim()) {
      errors.name = 'Name is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleBulkDownloadSubmit = async () => {
    if (!validateBulkDownloadForm()) return;

    try {
      const response = await bulkDownload(selectedIds, userEmail, userName);
      
      setBulkDownloadDrawerOpen(false);
      setSelectedIds([]);
      setUserEmail('');
      setUserName('');
      setFormErrors({});
      
      await showAlert({
        title: 'Download Request Submitted!',
        text: response.data || `Your download request for ${selectedIds.length} items has been queued. You will receive an email with the download link when ready.`,
        icon: 'success',
        confirmButtonText: 'OK'
      });
    } catch (error: any) {
      await showAlert({
        title: 'Error',
        text: error.message || 'Failed to submit bulk download request',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    }
  };

  const handleCloseDrawer = () => {
    setBulkDownloadDrawerOpen(false);
    setUserEmail('');
    setUserName('');
    setFormErrors({});
  };
  

  return (
    <AdminLayout
      activeMenuItem="/admin/accountspackitems"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
      requiredPermission='Manage.AccountsPackItem.View'
    >
      <Card
        title="Manage Accounts Pack Items"
        subtitle={`Total Records: ${totalRecords}`}
        headerActions={
          <div className="flex space-x-2">
            <AddNewButton
              onClick={() => navigate('/admin/accountspackitems/create')}
            />
          </div>
        }
      >
        <form onSubmit={handleSearch} className="mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <FormField
              id="emailSubject"
              label="Email Subject"
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              placeholder="Search by subject"
            />
            <FormField
              id="emailFrom"
              label="Email From"
              value={emailFrom}
              onChange={(e) => setEmailFrom(e.target.value)}
              placeholder="Search by email address"
            />
            <FormField
              id="periodYear"
              label="Period Year"
              type="dropdown"
              value={periodYear}
              onChange={(e) => setPeriodYear(e.target.value)}
              options={yearOptions}
              placeholder="All years"
            />
            <FormField
              id="periodMonth"
              label="Period Month"
              type="dropdown"
              value={periodMonth}
              onChange={(e) => setPeriodMonth(e.target.value)}
              options={monthOptions}
              placeholder="All months"
            />
          </div>

          <div className="flex space-x-4 mb-4">
            <Button variant="secondary" onClick={handleClearSearch}>
              Clear Search
            </Button>
            <Button type="submit">Search</Button>
            {selectedIds.length > 0 && (
              <Button
                onClick={() => setBulkDownloadDrawerOpen(true)}
                variant="primary"
              >
                <Download className="w-4 h-4 mr-2" />
                Download Selected ({selectedIds.length})
              </Button>
            )}
          </div>
        </form>

        <AccountsPackItemTable
          data={items}
          thumbnailUrls={thumbnailUrls}
          handleEdit={(dto) =>
            navigate(`/admin/accountspackitems/${dto.id}`)
          }
          handleDelete={handleDelete}
          currentPage={currentPage}
          totalPages={Math.ceil(totalRecords / pageSize)}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={isLoading}
          selectable={true}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
        />
      </Card>

      <Drawer isOpen={bulkDownloadDrawerOpen} onClose={handleCloseDrawer} title="Bulk Download Accounts Pack">
        <div className="space-y-6">
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-center gap-2 text-blue-700 dark:text-blue-300">
              <Download className="w-5 h-5" />
              <div>
                <p className="font-medium">
                  {selectedIds.length} {selectedIds.length === 1 ? 'item' : 'items'} selected
                </p>
                <p className="text-sm text-blue-600 dark:text-blue-400 mt-1">
                  A ZIP file will be created and emailed to you when ready
                </p>
              </div>
            </div>
          </div>

          <FormField
            id="userEmail"
            label="Your Email Address"
            value={userEmail}
            onChange={(e) => {
              setUserEmail(e.target.value);
              if (formErrors.email) setFormErrors({ ...formErrors, email: undefined });
            }}
            type="email"
            placeholder="your.email@example.com"
            required
            error={formErrors.email}
          />

          <FormField
            id="userName"
            label="Your Name"
            value={userName}
            onChange={(e) => {
              setUserName(e.target.value);
              if (formErrors.name) setFormErrors({ ...formErrors, name: undefined });
            }}
            placeholder="John Doe"
            required
            error={formErrors.name}
          />

          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <p className="text-sm text-yellow-800 dark:text-yellow-200">
              <strong>Note:</strong> The download link will be valid for 24 hours and will be sent to the email address provided above.
            </p>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              onClick={handleBulkDownloadSubmit}
              variant="primary"
              className="flex-1"
              disabled={isLoading}
            >
              {isLoading ? 'Processing...' : 'Request Download'}
            </Button>
            <Button
              onClick={handleCloseDrawer}
              variant="secondary"
              disabled={isLoading}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Drawer>
    </AdminLayout>
  );
};

export default AccountsPackItemList;